import axios from 'axios';
import { API_BASE_URL, endpoints } from './endpoints';

// Shared axios instance - send cookies for session auth
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Request/response types per OpenAPI spec ---

export interface SignupData {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  password: string;
  mobile: string;
  referralCode?: string;
}

export interface SignupResponse {
  message: string;
  userId: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface SendOtpRequest {
  userId: string;
  uniqueCode: string;
}

export interface VerifyOtpRequest {
  userId: string;
  uniqueCode: string;
  otp: string;
}

export interface UserResponse {
  _id?: string;
  userId: string;
  tenantId?: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  role?: string;
  registrationStatus?: 'pending' | 'completed';
  registeredOn?: string | null;
  lastLogin?: string | null;
  referralCode?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface VerifyEmailParams {
  token: string;
  userId: string;
}

export interface RegistrationUserInfoResponse {
  userId: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  role?: string;
  registrationMethod?: string;
}

export interface CompleteRegistrationData {
  token: string;
  userId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  password: string;
  mobile: string;
}

export interface MessageResponse {
  message: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  userId: string; // email - from reset link query
  password: string; // sent as newPassword
}

// --- API functions ---

/**
 * Sign up a new user
 * POST /api/auth/signup
 * Body: { userId, password, firstName, middleName?, lastName, mobile? }
 */
export const signup = async (data: SignupData): Promise<SignupResponse> => {
  const body = {
    userId: data.email,
    password: data.password,
    firstName: data.firstName,
    ...(data.middleName != null && data.middleName !== '' && { middleName: data.middleName }),
    lastName: data.lastName,
    ...(data.mobile && { mobile: data.mobile }),
    ...(data.referralCode && { referralCode: data.referralCode }),
  };
  const response = await apiClient.post<SignupResponse>(endpoints.auth.signup, body);
  return response.data;
};

/**
 * Login user (sets session cookie)
 * POST /api/auth/login
 * Body: { userId, password }
 */
export const login = async (data: LoginData): Promise<UserResponse> => {
  const response = await apiClient.post<UserResponse>(endpoints.auth.login, {
    userId: data.email,
    password: data.password,
  });
  return response.data;
};

/**
 * Send login OTP to email
 * POST /api/auth/send-otp
 * Body: { userId, uniqueCode }
 */
export const sendOtp = async (data: SendOtpRequest): Promise<MessageResponse> => {
  const response = await apiClient.post<MessageResponse>(endpoints.auth.sendOtp, data);
  return response.data;
};

/**
 * Resend login OTP (rate limited)
 * POST /api/auth/resend-otp
 * Body: { userId, uniqueCode }
 */
export const resendOtp = async (data: SendOtpRequest): Promise<MessageResponse> => {
  const response = await apiClient.post<MessageResponse>(endpoints.auth.resendOtp, data);
  return response.data;
};

/**
 * Verify login OTP (sets session cookie)
 * POST /api/auth/verify-otp
 * Body: { userId, uniqueCode, otp }
 */
export const verifyOtp = async (data: VerifyOtpRequest): Promise<UserResponse> => {
  const response = await apiClient.post<UserResponse>(endpoints.auth.verifyOtp, data);
  return response.data;
};

/**
 * Verify email link (GET with query params)
 * GET /api/auth/verify-email?token=&userId=  
 */
export const verifyEmailToken = async (params: VerifyEmailParams): Promise<MessageResponse> => {
  const response = await apiClient.get<MessageResponse>(endpoints.auth.verifyEmail, {
    params: { token: params.token, userId: params.userId },
  });
  return response.data;
};

/**
 * Get user info for complete-registration form
 * GET /api/auth/get-registration-user-info?token=&userId=
 */
export const getRegistrationUserInfo = async (
  token: string,
  userId: string
): Promise<RegistrationUserInfoResponse> => {
  const response = await apiClient.get<RegistrationUserInfoResponse>(
    endpoints.auth.getRegistrationUserInfo,
    { params: { token, userId } }
  );
  return response.data;
};

/**
 * Complete registration (admin-created user sets password)
 * POST /api/auth/complete-registration
 * Body: { token, userId, password, firstName?, middleName?, lastName?, mobile? }
 */
export const completeRegistration = async (
  data: CompleteRegistrationData
): Promise<MessageResponse> => {
  const body = {
    token: data.token,
    userId: data.userId,
    password: data.password,
    firstName: data.firstName,
    ...(data.middleName != null && data.middleName !== '' && { middleName: data.middleName }),
    lastName: data.lastName,
    ...(data.mobile && { mobile: data.mobile }),
  };
  const response = await apiClient.post<MessageResponse>(
    endpoints.auth.completeRegistration,
    body
  );
  return response.data;
};

/**
 * Request password reset email
 * POST /api/auth/forgot-password
 * Body: { userId }
 */
export const forgotPassword = async (data: ForgotPasswordData): Promise<MessageResponse> => {
  const response = await apiClient.post<MessageResponse>(endpoints.auth.forgotPassword, {
    userId: data.email,
  });
  return response.data;
};

/**
 * Reset password with token
 * POST /api/auth/reset-password
 * Body: { token, userId, newPassword }
 */
export const resetPassword = async (data: ResetPasswordData): Promise<MessageResponse> => {
  const response = await apiClient.post<MessageResponse>(endpoints.auth.resetPassword, {
    token: data.token,
    userId: data.userId,
    newPassword: data.password,
  });
  return response.data;
};

/**
 * Get current user (requires session cookie)
 * GET /api/me
 */
export const getCurrentUser = async (): Promise<UserResponse> => {
  const response = await apiClient.get<UserResponse>(endpoints.profile.me);
  return response.data;
};

export interface UpdateProfileData {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  mobile?: string;
}

/**
 * Update current user profile (no password)
 * POST /api/me
 * Body: { firstName?, middleName?, lastName?, mobile? }
 */
export const updateProfile = async (data: UpdateProfileData): Promise<UserResponse> => {
  const response = await apiClient.post<UserResponse>(endpoints.profile.updateProfile, data);
  return response.data;
};

/**
 * Logout (clears session cookie)
 * POST /api/me/logout
 */
export const logoutUser = async (): Promise<MessageResponse> => {
  const response = await apiClient.post<MessageResponse>(endpoints.profile.logout);
  return response.data;
};
